import React, { useEffect, useState } from "react";
import axios from "../api/baseurl";
import { useNavigate } from "react-router-dom";
import "./Spinner.css";
import "./Loader.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BiSolidCheckCircle, BiSolidXCircle } from "react-icons/bi";
import TestForPackage from "./testsByPackagePopup";
import AdminDashboard from "./admindashboard";
import { Pagination } from "react-bootstrap";
import { FaDownload } from "react-icons/fa";
import "./hm-theme.css";

function ReportsApproval() {
  const [appointmentDetails, setAppointmentDetails] = useState([]);
  const [loader, setLoader] = useState(true);
  const [filter, setFilter] = useState("REQUESTED");
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isTestsModalOpen, setIsTestsModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  const getData = async (pageNumber = 0, status = filter) => {
    const token = JSON.parse(localStorage.getItem("data")).token;

    setLoader(true);
    try {
      const response = await axios.get(`/api/employeeappointment/getAllUploadedReports`, {
        params: { page: pageNumber, status: status === "ALL" ? null : status },
        headers: { Authorization: `Bearer ${token}` },
      });
      setAppointmentDetails(response.data.content || []);
      setCurrentPage(response.data.number || 0);
      setTotalPages(response.data.totalPages || 0);
    } catch (error) {
      handleError(error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    getData();
  }, [filter]);

  const handlePageChange = (pageNumber) => {
    getData(pageNumber);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPageNumbersToShow = 5;
    let startPage = Math.max(0, currentPage - 2);
    let endPage = Math.min(totalPages - 1, currentPage + 2);

    if (currentPage <= 2) {
      endPage = Math.min(totalPages - 1, maxPageNumbersToShow - 1);
    }

    if (currentPage >= totalPages - 3) {
      startPage = Math.max(0, totalPages - maxPageNumbersToShow);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
          {i + 1}
        </Pagination.Item>
      );
    }
    return pageNumbers;
  };

  function handleError(error) {
    if (error.response?.status === 401) {
      navigate(`/signin`);
      toast.error("Your session has expired. Please login.");
    } else {
      toast.error(error.response?.data?.message || error.message || "An error occurred.");
    }
  }

  const handleViewReport = (reportId) => {
    const token = JSON.parse(localStorage.getItem("data")).token;
    axios({
      url: `/api/employee-appointment-reports/view/${reportId}`,
      method: "GET",
      responseType: "blob",
      headers: { Authorization: `Bearer ${token}` },
    }).then((response) => {
      const contentType = response.headers["content-type"] || "application/octet-stream";
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      window.open(blobUrl, "_blank");
    }).catch(() => toast.error("Failed to view report."));
  };

  const handleDownloadReport = (reportId, fileName) => {
    const token = JSON.parse(localStorage.getItem("data")).token;
    axios({
      url: `/api/employee-appointment-reports/view/${reportId}`,
      method: "GET",
      responseType: "blob",
      headers: { Authorization: `Bearer ${token}` },
    }).then((response) => {
      const contentType = response.headers["content-type"] || "application/octet-stream";
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", fileName || "report");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    }).catch(() => toast.error("Failed to download report."));
  };

  function rsubmit(e, id, status) {
    e.preventDefault();
    const token = JSON.parse(localStorage.getItem("data")).token;
    axios
      .post(
        "/api/employeeappointment/approve",
        { id, reportstatus: status },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(() => {
        toast.success(`Report ${status.toLowerCase()} successfully`);
        getData();
      })
      .catch((error) => {
        handleError(error);
      });
  }

  const openTestsModal = (pkg) => {
    setSelectedPackage(pkg);
    setIsTestsModalOpen(true);
  };

  const closeTestsModal = () => {
    setSelectedPackage(null);
    setIsTestsModalOpen(false);
  };

  const getStatusBadge = (status) => {
    if (!status) return "hm-badge hm-badge-info";
    const s = status.toUpperCase();
    if (s === "APPROVED") return "hm-badge hm-badge-success";
    if (s === "REJECTED") return "hm-badge hm-badge-danger";
    if (s === "REQUESTED") return "hm-badge hm-badge-warning";
    return "hm-badge hm-badge-info";
  };

  return (
    <div>
      <AdminDashboard />
      {loader && <div className="loading"></div>}

      <div className="col-12 mx-xl-auto ps-xl-10 mt-10 pe-xl-4">
        <div className="hm-page-wrapper">

          {/* Page Header */}
          <div className="hm-page-header mb-4">
            <div>
              <h2 className="hm-page-title">
                <i className="material-icons">fact_check</i>
                Report Approvals
              </h2>
              <p className="hm-page-subtitle">Review and approve uploaded employee medical reports</p>
            </div>
          </div>

          {/* Filter Card */}
          <div className="hm-card mb-4">
            <div className="hm-card-header">
              <h3 className="hm-card-title">
                <i className="material-icons">filter_list</i>
                Filter by Status
              </h3>
            </div>
            <div className="hm-card-body">
              <div className="row align-items-center">
                <div className="col-md-4">
                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="form-select"
                  >
                    <option value="ALL">All Reports</option>
                    <option value="REQUESTED">Requested (Pending Approval)</option>
                    <option value="APPROVED">Approved</option>
                    <option value="REJECTED">Rejected</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Table Card */}
          <div className="hm-card">
            <div className="hm-card-header">
              <h3 className="hm-card-title">
                <i className="material-icons">assignment</i>
                Uploaded Reports
              </h3>
              <span className="hm-badge hm-badge-primary">{appointmentDetails.length} records</span>
            </div>
            <div className="hm-card-body p-0">
              <div style={{ overflowX: "auto" }}>
                <table className="hm-table">
                  <thead>
                    <tr>
                      <th>Lab</th>
                      <th>Company</th>
                      <th>Employee</th>
                      <th>Date</th>
                      <th>Contact</th>
                      <th>Package / Tests</th>
                      <th>Appt Status</th>
                      <th>Reports</th>
                      <th>Action / Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appointmentDetails.length === 0 ? (
                      <tr>
                        <td colSpan="9">
                          <div className="hm-empty-state">
                            <i className="material-icons">assignment_turned_in</i>
                            <p>{loader ? "Loading reports..." : "No reports found for selected status"}</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      appointmentDetails.map((appointment) => (
                        <tr key={appointment.id}>
                          <td>{appointment.labs?.labname || "—"}</td>
                          <td>{appointment.company?.companyname || "—"}</td>
                          <td className="fw-semibold">
                            {appointment.emplo?.name} {appointment.emplo?.lastname}
                          </td>
                          <td>{appointment.date || "—"}</td>
                          <td>{appointment.emplo?.phoneno || "—"}</td>
                          <td>
                            {appointment.pack !== null && (
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <span className="hm-badge hm-badge-primary">{appointment.pack?.packagename}</span>
                                <i
                                  className="material-icons"
                                  style={{ cursor: "pointer", fontSize: "1.1rem", color: "#64748b" }}
                                  onClick={() => openTestsModal(appointment.packageid)}
                                  title="View Tests"
                                >info</i>
                              </div>
                            )}
                            {appointment.additionalTest && appointment.additionalTest.length > 0 && (
                              <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                                + {appointment.additionalTest.length} additional tests
                              </div>
                            )}
                          </td>
                          <td>
                            <span className="hm-badge hm-badge-info">
                              {appointment.appStatus?.appointmentStatus}
                            </span>
                          </td>
                          <td>
                            {appointment.orderStatus?.appointmentReport ? (
                              <a href={appointment.orderStatus.appointmentReport} target="_blank" rel="noopener noreferrer" className="hm-badge hm-badge-success text-decoration-none">
                                View
                              </a>
                            ) : (appointment.reports || []).length > 0 ? (
                              <ul className="list-unstyled mb-0">
                                {appointment.reports.map((report, ri) => (
                                  <li key={report.id} className="d-flex align-items-center gap-2 mb-1">
                                    <span
                                      className="text-primary"
                                      style={{ cursor: "pointer", fontSize: "0.78rem" }}
                                      onClick={() => handleViewReport(report.id)}
                                    >
                                      {report.fileName || `File ${ri + 1}`}
                                    </span>
                                    <FaDownload
                                      size={12}
                                      style={{ cursor: "pointer", color: "#198754" }}
                                      onClick={() => handleDownloadReport(report.id, report.fileName)}
                                    />
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <span className="text-muted small">No File</span>
                            )}
                          </td>
                          <td>
                            {appointment.reportstatus === "REQUESTED" ? (
                              <div className="d-flex align-items-center gap-2">
                                <span className="hm-badge hm-badge-warning">Requested</span>
                                <BiSolidCheckCircle
                                  style={{ color: "#059669", cursor: "pointer", fontSize: "1.4rem" }}
                                  title="Approve Report"
                                  onClick={(e) => rsubmit(e, appointment.id, "APPROVED")}
                                />
                                <BiSolidXCircle
                                  style={{ color: "#dc2626", cursor: "pointer", fontSize: "1.4rem" }}
                                  title="Reject Report"
                                  onClick={(e) => rsubmit(e, appointment.id, "REJECTED")}
                                />
                              </div>
                            ) : (
                              <span className={getStatusBadge(appointment.reportstatus)}>
                                {appointment.reportstatus || "N/A"}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-center py-3">
                  <Pagination className="hm-pagination">
                    <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0} />
                    <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0} />
                    {renderPageNumbers()}
                    <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1} />
                    <Pagination.Last onClick={() => handlePageChange(totalPages - 1)} disabled={currentPage >= totalPages - 1} />
                  </Pagination>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {isTestsModalOpen && (
        <TestForPackage
          isOpen={isTestsModalOpen}
          onRequestClose={closeTestsModal}
          packageId={selectedPackage}
        />
      )}
    </div>
  );
}

export default ReportsApproval;
